import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { BackofficeRole } from "@ac-expo/api-types";
import { isAxiosError } from "axios";
import {
  CreateGroupForm,
  UpdateGroupForm,
  GroupPayload,
  ImportGroupFromCsvForm,
} from "../schemas";
import groupQueryKeys from "../query-keys/group.query-keys";

const transformFormToPayload = (
  form: CreateGroupForm | UpdateGroupForm
): GroupPayload => ({
  name: form.name,
  type: form.type,
  visitorIds: form.visitorIds?.map((v) => v.id),
  exhibitorIds: form.exhibitorIds?.map((e) => e.id),
});

const createGroup = async (as: BackofficeRole, body: CreateGroupForm) => {
  try {
    const payload = transformFormToPayload(body);
    const { data } = await privateAxios.post(`/group/${as}`, payload);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useCreateGroupMutation = (as: BackofficeRole) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateGroupForm) => createGroup(as, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.as(as),
      });
    },
  });
};

const updateGroup = async (
  id: string,
  as: BackofficeRole,
  body: UpdateGroupForm
) => {
  try {
    const payload = transformFormToPayload(body);
    const { data } = await privateAxios.put(`/group/${as}/${id}`, payload);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpdateGroupMutation = (id: string, as: BackofficeRole) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateGroupForm) => updateGroup(id, as, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.getAs(id, as),
      });
    },
  });
};

const deleteGroup = async (id: string, as: BackofficeRole) => {
  try {
    const { data } = await privateAxios.delete(`/group/${as}/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useDeleteGroupMutation = (as: BackofficeRole) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGroup(id, as),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.all,
      });
    },
  });
};

const importGroupFromCsv = async (
  as: BackofficeRole,
  body: ImportGroupFromCsvForm
) => {
  try {
    const { data } = await privateAxios.post(`/group/${as}/import-csv`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useImportGroupFromCsvMutation = (as: BackofficeRole) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ImportGroupFromCsvForm) =>
      importGroupFromCsv(as, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.as(as),
      });
    },
  });
};

const addVisitorsToGroup = async (
  groupId: string,
  as: BackofficeRole,
  visitorIds: string[]
) => {
  try {
    const { data } = await privateAxios.post(
      `/group/${as}/${groupId}/visitors`,
      { visitorIds }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useAddVisitorsToGroupMutation = (
  groupId: string,
  as: BackofficeRole
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitorIds: string[]) =>
      addVisitorsToGroup(groupId, as, visitorIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.getAs(groupId, as),
      });
      queryClient.invalidateQueries({
        queryKey: [...groupQueryKeys.getAs(groupId, as), "available-visitors"],
      });
    },
  });
};

const removeVisitorFromGroup = async (
  groupId: string,
  as: BackofficeRole,
  visitorId: string
) => {
  try {
    const { data } = await privateAxios.delete(
      `/group/${as}/${groupId}/visitors/${visitorId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useRemoveVisitorFromGroupMutation = (
  groupId: string,
  as: BackofficeRole
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitorId: string) =>
      removeVisitorFromGroup(groupId, as, visitorId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.getAs(groupId, as),
      });
      queryClient.invalidateQueries({
        queryKey: [...groupQueryKeys.getAs(groupId, as), "available-visitors"],
      });
    },
  });
};

const addExhibitorsToGroup = async (
  groupId: string,
  as: BackofficeRole,
  exhibitorIds: string[]
) => {
  try {
    const { data } = await privateAxios.post(
      `/group/${as}/${groupId}/exhibitors`,
      { exhibitorIds }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useAddExhibitorsToGroupMutation = (
  groupId: string,
  as: BackofficeRole
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (exhibitorIds: string[]) =>
      addExhibitorsToGroup(groupId, as, exhibitorIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.getAs(groupId, as),
      });
      queryClient.invalidateQueries({
        queryKey: [...groupQueryKeys.getAs(groupId, as), "available-exhibitors"],
      });
    },
  });
};

const removeExhibitorFromGroup = async (
  groupId: string,
  as: BackofficeRole,
  exhibitorId: string
) => {
  try {
    const { data } = await privateAxios.delete(
      `/group/${as}/${groupId}/exhibitors/${exhibitorId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useRemoveExhibitorFromGroupMutation = (
  groupId: string,
  as: BackofficeRole
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (exhibitorId: string) =>
      removeExhibitorFromGroup(groupId, as, exhibitorId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupQueryKeys.getAs(groupId, as),
      });
      queryClient.invalidateQueries({
        queryKey: [...groupQueryKeys.getAs(groupId, as), "available-exhibitors"],
      });
    },
  });
};
